/**
 * Single row for full-event timeline view (all events, no compression).
 * Shows event type/label, timestamp, expandable payload; optional "Start game from here".
 */

import React, { useState } from 'react';

import { useTranslation } from '~/app';
import { getEventLabel } from '~/replay/eventLabels';
import type { EventLogEntry } from '~/server/src/dtos';
import { cn } from '~/utils/styles';

function formatEventTime(
  timestamp: number | Date,
  t: (key: string, opts?: { placeholders?: Record<string, string | number> }) => string,
): string {
  const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  if (diffMin < 1) return t('history_time_just_now');
  if (diffMin < 60) return t('history_time_min_ago', { placeholders: { count: diffMin } });
  if (diffHours < 24) return t('history_time_hours_ago', { placeholders: { count: diffHours } });
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export interface HistoryFullEventItemProps {
  entry: EventLogEntry;
  index: number;
  isActive: boolean;
  onSelect: () => void;
  /** When provided, show "Start game from here" and call with event index (state after this event = index + 1). */
  onSpawnFromHere?: () => void;
  /** Optional players for getEventLabel (e.g. for replay). */
  players?: Array<{ userId?: string; userid?: string; name?: string }>;
}

export const HistoryFullEventItem: React.FC<HistoryFullEventItemProps> = ({
  entry,
  index: _index,
  isActive,
  onSelect,
  onSpawnFromHere,
  players,
}) => {
  const { t } = useTranslation();
  const [payloadOpen, setPayloadOpen] = useState(false);

  const label = getEventLabel(entry, (key, opts) => t(key, opts), players);
  const timeLabel = formatEventTime(
    typeof entry.timestamp === 'number' ? entry.timestamp : new Date(entry.timestamp),
    t,
  );
  const hasPayload =
    entry.payload != null &&
    typeof entry.payload === 'object' &&
    Object.keys(entry.payload as object).length > 0;

  return (
    <div
      className={cn(
        'w-full rounded border-b border-amber-200/40 px-2 py-1.5 text-left transition-colors last:border-b-0',
        isActive
          ? 'bg-amber-500/20 outline-1 outline-offset-[-1px] outline-amber-500/50'
          : 'hover:bg-amber-500/10',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full flex-col gap-0.5 text-left focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
        aria-label={`${label}, ${timeLabel}`}
      >
        <div className="flex min-w-0 items-center justify-between gap-1">
          <span className="min-w-0 truncate text-xs font-medium text-amber-800">{label}</span>
          <span className="flex-shrink-0 text-[10px] text-stone-400 tabular-nums">{timeLabel}</span>
        </div>
        <span className="block truncate text-[10px] text-stone-500" title={entry.eventType}>
          {entry.eventType}
        </span>
      </button>
      {hasPayload && (
        <details
          className="mt-1"
          open={payloadOpen}
          onToggle={(e) => setPayloadOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer list-none text-[10px] text-amber-700">
            {payloadOpen ? t('history_payload_hide') : t('history_payload_show')}
          </summary>
          <pre className="mt-0.5 max-h-32 overflow-auto rounded bg-amber-50/80 p-1.5 text-left text-[10px]">
            {JSON.stringify(entry.payload, null, 2)}
          </pre>
        </details>
      )}
      {onSpawnFromHere && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSpawnFromHere();
          }}
          className="mt-1 rounded text-[10px] text-amber-700 underline hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          aria-label={t('history_start_game_from_here')}
        >
          {t('history_start_game_from_here')}
        </button>
      )}
    </div>
  );
};
